import React, { useEffect } from 'react';
import { Loader } from '@livechat/design-system-react-components';
import storeMetrics from '@livechat/store-metrics';
import AccountsSDK from '@livechat/accounts-sdk';
import { Global } from '@emotion/react';
import Cookies from 'js-cookie';

import Config from 'Config';
import { LocalStorageKey } from 'constants/local-storage';
import { getAccessToken, getScopes } from 'helpers/account';
import LocalStorage from 'services/LocalStorage';
import SignupTracking from 'services/SignupTracking';
import { TokenResponse } from 'services/connectivity/token/types';
import { Product } from 'types/product';
import { fetchTokenDetails } from 'hooks/use-get-token-details';
import { ApolloProvider } from '@apollo/client';
import { QueryClientProvider } from '@tanstack/react-query';
import { HelmetProvider } from 'react-helmet-async';
import { NotificationsProvider } from 'contexts/notifications/Provider';
import { getApolloClient } from 'services/ApolloClient';
import { getQueryClient } from 'services/ReactQueryClient';
import { globalStyles } from '_assets/css/global';

import Location from './services/Location';
import ChatWidget from './services/ChatWidget';
import Amplitude from './services/Amplitude';
import Clearbit from './services/Clearbit';
import GTM from './services/GTM';
import { Router } from './router/Router';
import { useSharedStore } from './stores/shared-store';

import * as styles from './App.styles';

const App = () => {
  const [tokenDetails, setTokenDetails] = React.useState<TokenResponse | null>(null);
  const [isTokenValid, setIsTokenValid] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(true);
  const [accessToken, setAccessToken] = React.useState(getAccessToken());
  const [scopes, setScopes] = React.useState(getScopes());

  const { set } = useSharedStore();

  const isVeryfing = window.location.href.includes('verification');

  useEffect(() => {
    if (Location.getUrlParam('utm_source') === LocalStorageKey.AccountsVerificationCodeReminder) {
      LocalStorage.set(LocalStorageKey.AccountsVerificationCodeReminder, true);
    }

    const couponCode = Location.getUrlParam('coupon_code');

    if (parseInt(Cookies.get('product'), 10) === Product.HelpDesk && couponCode) {
      LocalStorage.set(LocalStorageKey.HdCouponCode, couponCode);
    }
  }, []);

  useEffect(() => {
    const checkTokenStatus = async () => {
      if (!accessToken) {
        setIsTokenValid(false);
        setIsLoading(false);
        return;
      }

      try {
        const result = await fetchTokenDetails(accessToken);
        setTokenDetails(result);
        setIsTokenValid(true);
        Cookies.set('scope', scopes, { secure: true, sameSite: 'none' });
        Cookies.set('access_token', accessToken, { secure: true, sameSite: 'none' });
      } catch (err) {
        try {
          const sdk = new AccountsSDK({
            client_id: Config.organization_app_client_id,
            server_url: Config.accounts_url,
          });
          const response = await sdk.iframe().authorize();
          setTokenDetails(response);
          setScopes(response.scope);
          setAccessToken(response.access_token);
          const currentURL = new URL(window.location.href);
          const hashParams = new URLSearchParams(currentURL.hash.substring(1));
          hashParams.set('access_token', response.access_token);
          const updatedHash = hashParams.toString();
          currentURL.hash = updatedHash;
          const updatedURL = currentURL.toString();

          Cookies.set('scopes', response.scope, { secure: true, sameSite: 'none' });
          Cookies.set('access_token', response.access_token, { secure: true, sameSite: 'none' });
          window.location.replace(updatedURL);
          return;
        } catch (err2) {
          console.error(err2);
          setIsTokenValid(false);
          setIsLoading(false);
          return;
        }
      }

      // Remove the access_token and other unnecessary params from url
      if (window.location.pathname !== '/consent') {
        const currentURL = new URL(window.location.href);
        const params = new URLSearchParams(currentURL.hash.substring(1)); // Exclude the leading #
        params.delete('access_token');
        params.delete('token_type');
        params.delete('response_type');
        params.delete('expires_in');
        params.delete('scope');

        const updatedURL = `${currentURL.origin}${currentURL.pathname}${currentURL.search}#${params.toString()}`;
        window.location.replace(updatedURL);
      }

      setIsLoading(false);
    };

    if (!Location.isInLoginButtonIframe()) {
      ChatWidget.init();
      Amplitude.init();
      // Reads and stores various marketing parameters as cookies.
      set(storeMetrics());
      Clearbit.init();
    }

    GTM.init();

    if (!isVeryfing) {
      checkTokenStatus();
    } else {
      setIsLoading(false);
    }
  }, [isVeryfing]);

  useEffect(() => {
    SignupTracking.handlePreSignupEvent(tokenDetails?.organization_id);
  }, [tokenDetails?.organization_id]);

  if (isLoading) {
    return <Loader size="large" css={styles.loaderContainer} />;
  }

  return (
    <>
      <Global styles={globalStyles} />
      <QueryClientProvider client={getQueryClient()}>
        <ApolloProvider client={getApolloClient()}>
          <HelmetProvider>
            <NotificationsProvider>
              <div css={styles.flexContainer}>
                <div css={styles.wrapper}>
                  <Router isTokenValid={isTokenValid} />
                </div>
              </div>
            </NotificationsProvider>
          </HelmetProvider>
        </ApolloProvider>
      </QueryClientProvider>
    </>
  );
};

export default App;
